import React from 'react'
import { Edit, useForm } from '@refinedev/antd'
import { Form, Input, InputNumber, Select, Switch, Divider } from 'antd'
import type { Tables } from '@muaa/shared'
import { ImageUploadField } from '../../components/ImageUploadField'
import { GROUP_CATEGORY_OPTIONS } from './categoryOptions'

type Group = Tables<'groups'>

const PRIVACY_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'request', label: 'Request to join' },
  { value: 'invite', label: 'Invite only' },
]

/**
 * Group edit — every field shown on the mobile card/detail. `status` is NOT
 * editable here: the mobile card/detail never lets a member change a group's
 * status either, and here it has side effects (suspend/restore/archive all call
 * set_group_status) that a form save should not casually trigger, so those live
 * as explicit buttons on the Show page instead.
 */
export function GroupEdit() {
  const { formProps, saveButtonProps, form, query } = useForm<Group>()
  const currentCoverUrl: string | null = (query?.data?.data?.cover_url as string | null) ?? null
  const kind = query?.data?.data?.kind

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Form.Item label="Name" name="name" rules={[{ required: true, min: 3, max: 60 }]}>
          <Input disabled={kind === 'class_chapter'} />
        </Form.Item>
        <Form.Item label="Tagline" name="tagline" rules={[{ max: 90 }]}>
          <Input placeholder="One line under the name" />
        </Form.Item>
        <Form.Item label="About" name="about" rules={[{ max: 2000 }]}>
          <Input.TextArea rows={4} />
        </Form.Item>
        <Form.Item label="Category" name="category" rules={[{ required: true }]}>
          <Select options={GROUP_CATEGORY_OPTIONS} disabled={kind === 'class_chapter'} />
        </Form.Item>
        <Form.Item label="Tags" name="tags" tooltip="Up to 5 short tags">
          <Select mode="tags" tokenSeparators={[',']} maxCount={5} placeholder="Type a tag and press Enter" open={false} />
        </Form.Item>

        <Divider orientation="left">Cover</Divider>
        <ImageUploadField form={form} name="cover_url" label="Cover URL" currentUrl={currentCoverUrl} folder="groups" />
        <Form.Item label="Emblem icon" name="emblem_icon" tooltip="Icon name shown on the emblem/badge">
          <Input placeholder="e.g. bike" />
        </Form.Item>

        <Divider orientation="left">Access</Divider>
        <Form.Item label="Privacy" name="privacy" rules={[{ required: true }]}>
          <Select options={PRIVACY_OPTIONS} disabled={kind === 'class_chapter'} />
        </Form.Item>

        <Divider orientation="left">Where & when</Divider>
        <Form.Item label="Home city" name="home_city" rules={[{ max: 80 }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Latitude" name="lat">
          <InputNumber style={{ width: '100%' }} min={-90} max={90} step={0.0001} />
        </Form.Item>
        <Form.Item label="Longitude" name="lng">
          <InputNumber style={{ width: '100%' }} min={-180} max={180} step={0.0001} />
        </Form.Item>
        <Form.Item label="Virtual-first" name="is_virtual_first" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item label="Cadence label" name="cadence_label" tooltip="e.g. “Every Saturday · 6:30 AM”" rules={[{ max: 40 }]}>
          <Input />
        </Form.Item>

        <Divider orientation="left">Class range</Divider>
        <Form.Item label="Class year (min)" name="class_year_min">
          <Input placeholder="e.g. 1990" />
        </Form.Item>
        <Form.Item label="Class year (max)" name="class_year_max">
          <Input placeholder="e.g. 2020" />
        </Form.Item>

        <Divider orientation="left">Join</Divider>
        <Form.Item label="Join question" name="join_question" rules={[{ max: 200 }]}>
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item label="Guidelines" name="guidelines" rules={[{ max: 2000 }]}>
          <Input.TextArea rows={4} />
        </Form.Item>

        <Divider orientation="left">Discovery</Divider>
        <Form.Item label="Featured rank" name="featured_rank" tooltip="Lower shows first in the featured deck. Blank = not featured.">
          <InputNumber data-testid="group-featured-rank" style={{ width: '100%' }} min={0} />
        </Form.Item>
      </Form>
    </Edit>
  )
}
